// Enhanced Payroll Period Selector with Visual Status Indicators

import React, { useState, useMemo } from 'react';
import { format, addMonths, subMonths } from 'date-fns';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  Archive,
  Calculator,
  CheckCircle,
  Lock,
  TrendingUp,
  Users,
  Euro,
  DollarSign
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

import { 
  PayrollPeriod, 
  MonthStatus
} from '@/types/payroll-periods';
import { useBusinessCreationValidation } from '@/lib/business';
import { 
  createPayrollPeriod,
  generateAvailablePeriods,
  getStatusBadgeProps,
  createMonthStatus,
  getMonthStatusType 
} from '@/lib/payroll/month-status-utils';
import { formatEUR } from '@/lib/payroll';
import { cn } from '@/lib/core/utils';
import { BusinessDetails } from '@/types/index';
import { 
  UserMonthState, 
  getUserFriendlyMonthState, 
  createUserFriendlyMonthStatus,
  UserFriendlyMonthStatus,
  getUserFriendlyStateConfig
} from '@/lib/payroll/month-status-utils';
import { StateSpecificStatisticsProvider } from './StateSpecificStatisticsProvider';

export interface ConsolidatedPayrollStats {
  // Overview metrics - single source of truth for employee counts
  totalEmployees: number;
  employeeCoverage: {
    withPayroll: number;
    withShifts: number;
  };
  
  // Financial metrics - planning vs actual comparison
  grossPay: {
    planned?: number;
    actual?: number;
  };
  netPay: number;
  
  // Progress metrics
  paymentStatus: {
    pending: number;
    completed: number;
  };
  
  // Hours data - planning vs actual
  hours: {
    planned?: number;
    actual?: number;
  };
  
  // Compliance metrics
  complianceRate?: number;
  complianceIssues: {
    overScheduled: number;
    underScheduled: number;
  };
}

interface PayrollPeriodSelectorProps {
  selectedPeriod: PayrollPeriod | null;
  onPeriodChange: (period: PayrollPeriod) => void;
  monthStatuses: Map<string, MonthStatus>; // Key: "YYYY-MM"
  className?: string;
  countryCode?: string;
  business?: BusinessDetails | null;
  // Consolidated statistics - no more duplicates
  consolidatedStats?: ConsolidatedPayrollStats;
  // Selected period month status for dynamic state determination
  selectedPeriodStatus?: MonthStatus | null;
}

export const PayrollPeriodSelector: React.FC<PayrollPeriodSelectorProps> = ({
  selectedPeriod,
  onPeriodChange,
  monthStatuses,
  className,
  countryCode,
  business,
  consolidatedStats,
  selectedPeriodStatus
}) => {
  // Removed calendar popup state - month selector is always visible
  const [selectedYear, setSelectedYear] = useState(selectedPeriod?.year || new Date().getFullYear());
  
  // Use centralized month availability service
  const {
    isBeforeBusinessCreation,
    isYearNavigationDisabled,
    businessCreationYear
  } = useBusinessCreationValidation(business);
  
  // Generate available periods (12 months back, 3 months forward)
  const availablePeriods = useMemo(() => {
    return generateAvailablePeriods(new Date(), 12, 3);
  }, []);
  
  // Update selected year when selected period changes
  React.useEffect(() => {
    if (selectedPeriod?.year) {
      setSelectedYear(selectedPeriod.year);
    }
  }, [selectedPeriod?.year]);
  
  // Get current month status
  const currentMonthStatus = useMemo(() => {
    if (!selectedPeriod) return undefined;
    const key = `${selectedPeriod.year}-${String(selectedPeriod.month).padStart(2, '0')}`;
    return monthStatuses.get(key);
  }, [selectedPeriod, monthStatuses]);
  
  // Quick navigation handlers
  const goToPreviousMonth = () => {
    if (!selectedPeriod) return;
    const newDate = subMonths(selectedPeriod.date, 1);
    onPeriodChange(createPayrollPeriod(newDate));
  };
  
  const goToNextMonth = () => {
    if (!selectedPeriod) return;
    const newDate = addMonths(selectedPeriod.date, 1);
    onPeriodChange(createPayrollPeriod(newDate));
  };
  
  const goToCurrentMonth = () => {
    onPeriodChange(createPayrollPeriod(new Date()));
  };
  
  
  // Get icon for month status type
  const getStatusIcon = (monthStatus?: MonthStatus) => {
    if (!monthStatus) return Clock;
    
    switch (monthStatus.type) {
      case 'current_incomplete':
        return Clock;
      case 'last_complete':
        return monthStatus.status === 'paid' ? CheckCircle : Calculator;
      case 'historical':
        return Archive;
      case 'future':
        return TrendingUp;
      case 'locked':
        return Lock;
      default:
        return Clock;
    }
  };
  
  // Format currency based on country
  const formatCurrency = (amount: number) => {
    if (countryCode) {
      return formatEUR(amount);
    }
    return `$${amount.toFixed(2)}`;
  };
  
  return (
    <div className={cn("space-y-4", className)}>
        {/* Main Period Selector */}
        <Card>
          <CardContent className="p-6">
            {/* Two-column layout: 70% left, 30% right */}
            <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
              {/* Left Column - Current Selection & Status Info (70%) */}
              <div className="lg:col-span-7 space-y-4">
                {/* Current Selection Display */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    {React.createElement(getStatusIcon(currentMonthStatus), { 
                      className: "h-4 w-4 text-muted-foreground" 
                    })}
                    <div>
                      <h3 className="font-semibold">{selectedPeriod?.displayName || 'No Period Selected'}</h3>
                      {currentMonthStatus && (
                        <div className="flex items-center gap-2 mt-1">
                          <Badge 
                            variant={getStatusBadgeProps(currentMonthStatus).variant}
                            className={cn("text-xs", getStatusBadgeProps(currentMonthStatus).color)}
                          >
                            {getStatusBadgeProps(currentMonthStatus).label}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Dynamic State-Specific Statistics */}
                {consolidatedStats && (
                  <StateSpecificStatisticsProvider
                    userState={getUserFriendlyMonthState(
                      selectedPeriodStatus || currentMonthStatus || {
                        period: selectedPeriod || createPayrollPeriod(new Date()),
                        type: getMonthStatusType(selectedPeriod || createPayrollPeriod(new Date())),
                        status: 'draft',
                        completionPercentage: 0,
                        employeeCount: 0,
                        employeesWithPayroll: 0,
                        totalGrossPay: 0,
                        totalNetPay: 0,
                        canEdit: false,
                        canCalculate: false,
                        canFinalize: false,
                        canExport: false,
                        warnings: [],
                        actions: []
                      }
                    )}
                    consolidatedStats={consolidatedStats}
                    countryCode={countryCode}
                  />
                )}
              </div>
              
              {/* Right Column - Month Selector (30%) */}
              <div className="lg:col-span-3 space-y-3">
                {/* Year Navigation */}
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      const newYear = selectedYear - 1;
                      if (!isYearNavigationDisabled(newYear, 'previous')) {
                        setSelectedYear(newYear);
                      }
                    }}
                    disabled={isYearNavigationDisabled(selectedYear - 1, 'previous')}
                    className="h-7 w-7"
                  >
                    <ChevronLeft className="h-3 w-3" />
                    <span className="sr-only">Previous Year</span>
                  </Button>
                  
                  <span className="text-lg font-semibold">{selectedYear}</span>
                  
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      setSelectedYear(selectedYear + 1);
                    }}
                    className="h-7 w-7"
                  >
                    <ChevronRight className="h-3 w-3" />
                    <span className="sr-only">Next Year</span>
                  </Button>
                </div>
                
                {/* Month Grid with Payroll Status */}
                <div className="grid grid-cols-4 lg:grid-cols-3 gap-2">
                  {Array.from({ length: 12 }, (_, index) => {
                    const monthDate = new Date(selectedYear, index, 1);
                    const period = createPayrollPeriod(monthDate);
                    const isSelected = selectedPeriod && period.year === selectedPeriod.year && period.month === selectedPeriod.month;
                    const isDisabled = isBeforeBusinessCreation(index, selectedYear);
                    
                    // Get user-friendly state and styling
                    let userState: UserMonthState = 'PROCESSED';
                    let label = 'Month';
                    let badgeColorClass = 'text-gray-500 border-gray-300 dark:text-gray-400 dark:border-gray-600';

                    if (isDisabled) {
                      label = 'N/A';
                      badgeColorClass = 'text-gray-400 border-gray-200 bg-gray-50 dark:text-gray-500 dark:border-gray-700 dark:bg-gray-800/30';
                    } else {
                      // Create a minimal month status for state determination
                      const mockMonthStatus = {
                        period,
                        type: getMonthStatusType(period),
                        status: 'draft' as const,
                        completionPercentage: 0,
                        employeeCount: 0,
                        employeesWithPayroll: 0,
                        totalGrossPay: 0,
                        totalNetPay: 0,
                        canEdit: false,
                        canCalculate: false,
                        canFinalize: false,
                        canExport: false,
                        warnings: [],
                        actions: []
                      };

                      userState = getUserFriendlyMonthState(mockMonthStatus);
                      const stateConfig = getUserFriendlyStateConfig(userState);
                      label = stateConfig.label;

                      // Apply state-specific badge colors that match statistics theming
                      switch (userState) {
                        case 'FUTURE':
                          badgeColorClass = 'text-primary border-primary/30 bg-primary/10 hover:bg-primary/20';
                          break;
                        case 'UPCOMING':
                          badgeColorClass = 'text-blue-700 border-blue-300 bg-blue-50 hover:bg-blue-100 dark:text-blue-300 dark:border-blue-700 dark:bg-blue-900/30 dark:hover:bg-blue-900/50';
                          break;
                        case 'IN_PROGRESS':
                          badgeColorClass = 'text-green-700 border-green-300 bg-green-50 hover:bg-green-100 dark:text-green-300 dark:border-green-700 dark:bg-green-900/30 dark:hover:bg-green-900/50';
                          break;
                        case 'TO_BE_PROCESSED':
                          badgeColorClass = 'text-orange-700 border-orange-300 bg-orange-50 hover:bg-orange-100 dark:text-orange-300 dark:border-orange-700 dark:bg-orange-900/30 dark:hover:bg-orange-900/50';
                          break;
                        case 'PROCESSED':
                          badgeColorClass = 'text-gray-700 border-gray-300 bg-gray-50 hover:bg-gray-100 dark:text-gray-300 dark:border-gray-600 dark:bg-gray-800/50 dark:hover:bg-gray-800/70';
                          break;
                      }
                    }
                    
                    return (
                      <Button
                        key={index}
                        variant={isSelected ? "default" : "outline"}
                        size="sm"
                        className="h-12 p-1 flex flex-col items-center justify-center text-center gap-0.5"
                        disabled={isDisabled}
                        onClick={() => {
                          onPeriodChange(period);
                        }}
                      >
                        <span className="text-xs font-medium">
                          {format(monthDate, 'MMM')}
                        </span>
                        <Badge 
                          variant="outline" 
                          className={cn(
                            "text-[8px] px-1 py-0 h-4 rounded-sm leading-none font-medium",
                            isSelected && "text-primary-foreground border-primary-foreground bg-primary",
                            !isSelected && badgeColorClass
                          )}
                        >
                          {label}
                        </Badge>
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
    </div>
  );
};